import { useMemo, useCallback } from "react";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import { ConfigScope } from "@shared/schemas/config-scope.schemas";
import { isFeatureConfig } from "@shared/schemas/resolved-feature-config.schemas";
import type { ConfigContext } from "@shared/schemas/config-context.schemas";
import type {
  ResolvedFeatureConfigUserView,
  TypedResolvedFeatureConfigUserView,
} from "@shared/schemas/resolved-feature-config.schemas";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";

export interface UseFeatureConfigsInput {
  readonly context: ConfigContext;
  readonly enabled?: boolean;
}

export type FeatureEnabledMapType = Map<FeatureKey, boolean>;

export type FeatureFlagStateType = boolean | "loading";

export interface UseFeatureConfigsOutput {
  readonly configs: readonly ResolvedFeatureConfigUserView[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => Promise<void>;
  readonly enabledMap: FeatureEnabledMapType;
  readonly isFeatureEnabled: (featureKey: FeatureKey) => FeatureFlagStateType;
  readonly isFeatureAvailable: (featureKey: FeatureKey) => FeatureFlagStateType;
  readonly getFeatureConfig: <K extends FeatureKey>(
    featureKey: K,
  ) => TypedResolvedFeatureConfigUserView<K> | undefined;
}

export function useFeatureConfigs(
  input: UseFeatureConfigsInput,
): UseFeatureConfigsOutput {
  const { context, enabled = true } = input;

  const { data, isLoading, error, refetch } =
    useUserActions().useGetFeatureConfigs({ context, enabled });

  const configs = useMemo(() => data ?? [], [data]);

  const enabledMap = useMemo((): FeatureEnabledMapType => {
    const map = new Map<FeatureKey, boolean>();
    for (const config of configs) {
      map.set(config.featureKey, config.enabled);
    }
    return map;
  }, [configs]);

  const isFeatureEnabled = useCallback(
    (featureKey: FeatureKey): FeatureFlagStateType => {
      if (isLoading) {
        return "loading";
      }
      if (configs.length === 0) {
        return false;
      }
      return enabledMap.get(featureKey) ?? false;
    },
    [enabledMap, isLoading, configs.length],
  );

  const isFeatureAvailable = useCallback(
    (featureKey: FeatureKey): FeatureFlagStateType => {
      if (isLoading) {
        return "loading";
      }
      if (configs.length === 0) {
        return false;
      }
      const featureConfig = configs.find(
        (c: ResolvedFeatureConfigUserView) => c.featureKey === featureKey,
      );
      if (featureConfig === undefined) {
        return false;
      }
      if (featureConfig.enabled) {
        return true;
      }
      return !ConfigScope.isAdminLevel(featureConfig.sources.enabled);
    },
    [configs, isLoading],
  );

  const getFeatureConfig = useCallback(
    <K extends FeatureKey>(
      featureKey: K,
    ): TypedResolvedFeatureConfigUserView<K> | undefined => {
      if (isLoading || configs.length === 0) {
        return undefined;
      }
      const featureConfig = configs.find(
        (
          c: ResolvedFeatureConfigUserView,
        ): c is TypedResolvedFeatureConfigUserView<K> =>
          isFeatureConfig(c, featureKey),
      );
      return featureConfig;
    },
    [configs, isLoading],
  );

  const handleRefetch = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return {
    configs,
    isLoading,
    error,
    isFeatureEnabled,
    isFeatureAvailable,
    getFeatureConfig,
    enabledMap,
    refetch: handleRefetch,
  };
}
